import type { FetchResponse, RestService } from '@sydle/ui-api';
import { CustomAPI } from '@sydle/ui-api';
import type { ClassesMetadatas, FieldsMetadatas } from '../components/forms/one-form/one-field/form-data-helper';
import type { EObjectStorage, Reference } from '../models';
import type { MetadataOptions } from '../models/Metadata';
import { FORM_API_CLASS_ID } from './constants';
import { FormInterceptor } from './form/form-interceptor';
import type { PatchOperation } from './object-api';

export type GetFormArgs = {
  reference: Reference;
  storage: EObjectStorage;
  translate: boolean;
  option?: MetadataOptions;
  filteredField?: string;
  objectOnly?: boolean;
};

export type GetFormResponse = {
  object: any;
  metadatas?: ClassesMetadatas;
  fields?: FieldsMetadatas;
};

export type PatchFormArgs = {
  reference: Reference;
  storage: Exclude<EObjectStorage, EObjectStorage.history>;
  translate: boolean;
  operations: PatchOperation[];
  filteredField?: string;
};

export enum FormMethods {
  PATCH = 'patchForm',
  GET = 'getForm',
}

export type PatchFormResponse = GetFormResponse;

export class FormAPI {
  private formAPI: CustomAPI;

  constructor(restService: RestService) {
    this.formAPI = new CustomAPI(FORM_API_CLASS_ID, restService);
    restService.registerInterceptor(new FormInterceptor());
  }

  public async getForm(args: GetFormArgs): Promise<FetchResponse<GetFormResponse>> {
    const response = await this.formAPI.fetch(
      'getForm',
      {
        reference: args.reference,
        storage: args.storage,
        option: args.option,
        filteredField: args.filteredField,
        objectOnly: args.objectOnly,
      },
      {
        queryParams: {
          _translate: args.translate,
        },
      } as any,
    );

    if (response.error) return { error: response.error };

    return { data: response.data as GetFormResponse };
  }

  public async patchForm(args: PatchFormArgs): Promise<FetchResponse<PatchFormResponse>> {
    const response = await this.formAPI.fetch(
      'patchForm',
      {
        reference: args.reference,
        storage: args.storage,
        operations: args.operations,
        filteredField: args.filteredField,
      },
      {
        forcePost: true,
        queryParams: {
          _translate: args.translate,
        },
      } as any,
    );

    if (response.error) return { error: response.error };

    return { data: response.data as PatchFormResponse };
  }
}
